import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { HashTag, TagSelectOption } from '@atlassian/tc-tag-picker';
import { TagUsage } from '@atlassian/tc-tag-picker/dist/types/types';
import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useRouter } from 'react-resource-router';

import { useAnalytics } from '@townsquare/analytics';
import { getStringColorHash } from '@townsquare/theme';
import { topicRoute } from '@townsquare/topic-view/route';
import { useMetaClickHandler } from '@townsquare/ui-interactions';

import * as styles from './styles';

const tagColors = ['greenLight', 'tealLight', 'blueLight', 'purpleLight', 'redLight', 'yellowLight'];

const tagHtmlBackgroundColor: Record<string, string> = {
  greenLight: token('color.background.accent.green.subtler'),
  tealLight: token('color.background.accent.teal.subtler'),
  blueLight: token('color.background.accent.blue.subtler'),
  purpleLight: token('color.background.accent.purple.subtler'),
  redLight: token('color.background.accent.red.subtler'),
  yellowLight: token('color.background.accent.orange.subtler'),
};

const tagHtmlForegroundColor: Record<string, string> = {
  greenLight: token('color.icon.accent.green'),
  tealLight: token('color.icon.accent.teal'),
  blueLight: token('color.icon.accent.blue'),
  purpleLight: token('color.icon.accent.purple'),
  redLight: token('color.icon.accent.red'),
  yellowLight: token('color.icon.accent.orange'),
};

type TagItemProps = {
  tag: TagSelectOption;
  onRemove?: (tag: TagSelectOption) => void;
};

export const getTagColor = (tagName: string) =>
  tagColors[Math.abs(getStringColorHash(tagName)) % tagColors.length] as any;
export const getTagHtmlColor = (tagName: string) => tagHtmlBackgroundColor[getTagColor(tagName)] as string;
export const getTagForegroundColor = (tagName: string) => tagHtmlForegroundColor[getTagColor(tagName)] as string;

// TODO: This is a temporary fix to get the usage total
export const getTagUsageTotal = (usage: TagUsage) => {
  return usage
    ? Object.entries(usage)
        .map(([key, values]) => {
          if (key !== 'followerCount') {
            return values;
          }
        })
        .reduce<number>((acc, current) => acc + (current ?? 0), 0)
    : 0;
};

export const TagItem = (props: { onRemove?: (tag: TagSelectOption) => void; tag: TagSelectOption }) => {
  return <BaseTagItem tag={props.tag} onRemove={props.onRemove} />;
};

export const ViewOnlyTagItem = (props: { tag: TagSelectOption }) => {
  return <BaseTagItem tag={props.tag} />;
};

const BaseTagItem = ({ tag, onRemove }: TagItemProps) => {
  const [routerStore] = useRouter();
  const canEdit = Boolean(onRemove);
  const onAfterRemove = useCallback(() => {
    onRemove?.(tag);
  }, [tag, onRemove]);

  const analytics = useAnalytics();
  const intl = useIntl();

  const { path } = useMetaClickHandler(topicRoute, { params: { uuid: tag.value } });

  const tagTooltipContent = (
    <styles.ToolTipContent>
      <styles.ToolTipHashTag>
        <span
          onClick={() => {
            void analytics.ui('label', 'clicked', {
              labelId: tag.value,
              screen: routerStore.route.name,
            });
          }}
          data-testid={`tag-item-${tag.id}-in-hover-card`}
        >
          <HashTag label={tag.label ?? ''} href={path} />
        </span>
      </styles.ToolTipHashTag>
      <styles.ToolTipDescription> {tag.description || ''} </styles.ToolTipDescription>
      <styles.ToolTipFooter>
        <FormattedMessage
          id="townsquare.tags.tag-item.tooltip-footer"
          defaultMessage="Used in {usageCount, plural, one {# place} other {# places}}"
          description="Used in places"
          values={{ usageCount: tag.usage ? getTagUsageTotal(tag.usage) : 0 }}
        />
      </styles.ToolTipFooter>
    </styles.ToolTipContent>
  );

  const tooltipComponent = styled(TooltipPrimitive)({
    background: token('elevation.surface.overlay'),
    borderRadius: '4px',
    boxShadow: token('elevation.shadow.overlay'),
    boxSizing: 'content-box' /* do not set this to border-box or it will break the overflow handling */,
    color: token('color.text'),
    maxHeight: '300px',
    maxWidth: '300px',
    padding: `${token('space.100')} ${token('space.150')}`,
  });

  return (
    <Tooltip component={tooltipComponent} content={tagTooltipContent} position="bottom">
      <span
        onClick={() => {
          void analytics.ui('label', 'clicked', {
            labelId: tag.value,
            screen: routerStore.route.name,
          });
        }}
        data-testid={`tag-item-${tag.id}`}
      >
        <HashTag
          href={path}
          label={tag.label ?? ''}
          isRemovable={canEdit}
          removeButtonLabel={
            canEdit
              ? intl.formatMessage({
                  id: 'townsquare.tags.tag-item.remove-tag-label',
                  defaultMessage: 'Remove tag',
                  description: 'Remove tag',
                })
              : undefined
          }
          onAfterRemoveAction={canEdit ? onAfterRemove : undefined}
        />
      </span>
    </Tooltip>
  );
};
